import { Button, Typography, styled } from '@ethings/ui-toolkit';
import { Box, useTheme } from '@mui/material';
import { SxProps } from '@mui/system';
import chroma from 'chroma-js';
import { FC, useEffect, useState } from 'react';
import { APP_BANNER_HEIGHT } from '../../constants';
import { useResponsiveText } from '../../hooks';
import { innerText } from '../../util';

interface Props {
  actionLabel?: string;
  background?: string;
  'data-testid'?: string;
  label?: string;
  position?: 'static' | 'relative' | 'absolute';
  onAction?: () => void;
}

export const AppBanner: FC<Props> = ({
  actionLabel,
  background,
  label,
  onAction,
  position = 'relative',
  ...props
}) => {
  const theme = useTheme();
  const [positionStyles, setPositionStyles] = useState<SxProps>({ top: '0', left: '0' });

  const labelLength = innerText(label ?? '').length;
  const actionLabelLength = innerText(actionLabel ?? '').length;

  const labelStyles = useResponsiveText('0.875rem', labelLength, {
    minFontSize: '0.75rem',
    maxFontSize: '0.875rem',
    textLengthThreshold: 30,
  });

  const actionLabelStyles = useResponsiveText('0.875rem', actionLabelLength, {
    minFontSize: '0.75rem',
    maxFontSize: '0.875rem',
    textLengthThreshold: 20,
  });

  useEffect(() => {
    setPositionStyles(position === 'relative' ? { top: '0', left: '0' } : {});
  }, [position]);

  if (!background) {
    background = theme.palette.primary.main;
  }

  const textColor = (chroma(background).luminance() < 0.5) ? theme.palette.common.white : theme.palette.common.black;

  const BannerActionButton = styled(Button)({
    textTransform: 'none',
    fontWeight: 'bold',
    ...actionLabelStyles,
  });

  return (
    <Box
      {...props}
      sx={{
        width: 1,
        backgroundColor: background,
        color: textColor,
        position,
        textAlign: 'center',
        py: actionLabel ? 0 : 1,
        minHeight: APP_BANNER_HEIGHT,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: '20',
        ...positionStyles,
      }}
    >
      <Typography
        component='span'
        data-testid='banner-label'
        variant='body2'
        mr={1}
        style={{
          fontSize: labelStyles.fontSize,
          lineHeight: labelStyles.lineHeight,
          wordBreak: labelStyles.wordBreak,
          whiteSpace: labelStyles.whiteSpace,
          overflow: labelStyles.overflow,
          textOverflow: labelStyles.textOverflow,
        }}
      >
        {label}
      </Typography>
      {actionLabel && <BannerActionButton data-testid='banner-action' variant='text' onClick={onAction} color='inherit'>{actionLabel}</BannerActionButton>}
    </Box >
  );
};
